import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Upload, Eye, Download, Trash2, RefreshCcw, FileImage } from "lucide-react";
import { toast } from "sonner";

const nowMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtSize = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function Schedule() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(nowMonth());
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/schedules", { params: { month } });
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat schedule");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const monthLabel = useMemo(() => {
    try {
      const [y, m] = month.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
    } catch {
      return month;
    }
  }, [month]);

  const doUpload = async () => {
    if (!file) {
      toast.error("Pilih file dulu");
      return;
    }
    const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      toast.error("Format harus PDF/JPG/PNG");
      return;
    }
    const formData = new FormData();
    formData.append("month", month);
    formData.append("file", file);
    setUploading(true);
    try {
      await api.post("/schedules/upload", formData);
      toast.success("Schedule berhasil diupload");
      setFile(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal upload file");
    } finally {
      setUploading(false);
    }
  };

  const openPreview = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.get(`/schedules/${row.id}/file`, {
        params: { download: false },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuka file");
    } finally {
      setBusyId("");
    }
  };

  const doDownload = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.get(`/schedules/${row.id}/file`, {
        params: { download: true },
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = row.file_name || "schedule-file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal download file");
    } finally {
      setBusyId("");
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Hapus file ${row.file_name}?`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/schedules/${row.id}`);
      toast.success("File berhasil dihapus");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus file");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <div className="label-ind">Document Center</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Schedule</h1>
          <p className="text-sm text-slate-500 mt-1">Upload dan arsip schedule bulanan dalam format PDF/JPG/PNG.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm">
          <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card className="rounded-sm border-slate-200 bg-white p-4 mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-end">
          <div>
            <div className="label-ind mb-1">Bulan Schedule</div>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-sm" />
          </div>
          <div>
            <div className="label-ind mb-1">File</div>
            <Input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="h-9 rounded-sm file:mr-2 file:text-xs"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={doUpload} disabled={uploading || !user} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
              <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploading..." : "Upload"}
            </Button>
            {!user && <div className="text-xs text-amber-700 self-center">Login diperlukan untuk upload/hapus.</div>}
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-500">
          Format nama otomatis: <span className="font-mono">SCH_BULAN-TAHUN_vXX</span> · Max file: <span className="font-mono">15 MB</span> · Bulan aktif: <span className="font-mono">{monthLabel}</span>
        </div>
      </Card>

      <Card className="rounded-sm border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th className="text-left">Nama File</th>
                <th className="text-left">Bulan</th>
                <th className="text-left">Uploader</th>
                <th className="text-left">Waktu Upload</th>
                <th className="text-right">Ukuran</th>
                <th className="text-right w-44">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">Memuat data...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-400">Belum ada file untuk bulan ini.</td>
                </tr>
              )}
              {!loading && rows.map((row) => (
                <tr key={row.id}>
                  <td className="font-medium text-slate-800">
                    <div className="flex items-center gap-2">
                      <FileImage className="w-4 h-4 text-slate-500" />
                      <span>{row.file_name}</span>
                    </div>
                  </td>
                  <td className="font-mono">{row.month}</td>
                  <td>{row.uploaded_by || "-"}</td>
                  <td className="font-mono text-xs">{row.created_at ? new Date(row.created_at).toLocaleString("id-ID") : "-"}</td>
                  <td className="text-right font-mono">{fmtSize(row.size_bytes)}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openPreview(row)} disabled={busyId === row.id}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => doDownload(row)} disabled={busyId === row.id}>
                        <Download className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => doDelete(row)} disabled={!user || busyId === row.id}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
